import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, X, SlidersHorizontal, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useSessionTypes } from "../hooks/useSessionTypes";
import { SessionCard } from "../components/SessionCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { toast } from "../components/ui/sonner";
import { formatDate } from "../lib/dateUtils";

const RADIUS_OPTIONS = [
  { value: "10", label: "Within 10 miles" },
  { value: "25", label: "Within 25 miles" },
  { value: "50", label: "Within 50 miles" },
  { value: "100", label: "Within 100 miles" },
  { value: "any", label: "Anywhere" },
];
const DATE_OPTIONS = [
  { value: "any", label: "Any Date" },
  { value: "today", label: "Today" },
  { value: "weekend", label: "This Weekend" },
  { value: "next7", label: "Next 7 Days" },
  { value: "custom", label: "Choose a Date" },
];
const SORT_OPTIONS = [
  { value: "soonest", label: "Soonest" },
  { value: "closest", label: "Closest" },
  { value: "lowest_price", label: "Lowest Price" },
  { value: "newest", label: "Newest" },
];
const PRICE_BOUNDS = [0, 300];
const DEFAULT_FILTERS = {
  locationText: "", lat: null, lng: null,
  radius: "25", sessionType: "all", datePreset: "any", customDate: "",
  priceMin: PRICE_BOUNDS[0], priceMax: PRICE_BOUNDS[1], sort: "soonest",
};

function filtersFromParams(params) {
  return {
    locationText: params.get("location") || "",
    lat: params.get("lat") ? parseFloat(params.get("lat")) : null,
    lng: params.get("lng") ? parseFloat(params.get("lng")) : null,
    radius: params.get("radius") || DEFAULT_FILTERS.radius,
    sessionType: params.get("type") || "all",
    datePreset: params.get("date") || "any",
    customDate: params.get("custom_date") || "",
    priceMin: params.get("price_min") ? Number(params.get("price_min")) : PRICE_BOUNDS[0],
    priceMax: params.get("price_max") ? Number(params.get("price_max")) : PRICE_BOUNDS[1],
    sort: params.get("sort") || "soonest",
  };
}

function paramsFromFilters(f) {
  const p = {};
  if (f.locationText) p.location = f.locationText;
  if (f.lat != null) p.lat = f.lat;
  if (f.lng != null) p.lng = f.lng;
  if (f.radius !== DEFAULT_FILTERS.radius) p.radius = f.radius;
  if (f.sessionType !== "all") p.type = f.sessionType;
  if (f.datePreset !== "any") p.date = f.datePreset;
  if (f.customDate) p.custom_date = f.customDate;
  if (f.priceMin !== PRICE_BOUNDS[0]) p.price_min = f.priceMin;
  if (f.priceMax !== PRICE_BOUNDS[1]) p.price_max = f.priceMax;
  if (f.sort !== "soonest") p.sort = f.sort;
  return p;
}

export default function FindSession() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionTypes = useSessionTypes();
  const [filters, setFilters] = useState(() => filtersFromParams(searchParams));
  const [locationInput, setLocationInput] = useState(() => {
    const f = filtersFromParams(searchParams);
    return f.locationText || (f.lat != null ? "Current Location" : "");
  });
  const [results, setResults] = useState([]);
  const [resolvedLocation, setResolvedLocation] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [geoLoading, setGeoLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);

  const abortRef = useRef(null);
  const skipNextSync = useRef(false);

  const runSearch = useCallback(async (f) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const params = {};
      if (f.lat != null && f.lng != null) {
        params.lat = f.lat;
        params.lng = f.lng;
      } else if (f.locationText) {
        params.location = f.locationText;
      }
      if (f.radius !== "any" && (f.lat != null || f.locationText)) params.radius = f.radius;
      if (f.sessionType !== "all") params.session_type = f.sessionType;
      if (f.datePreset === "custom" && f.customDate) {
        params.date_from = f.customDate;
        params.date_to = f.customDate;
      } else if (f.datePreset !== "any") {
        params.date_preset = f.datePreset;
      }
      if (f.priceMin !== PRICE_BOUNDS[0]) params.price_min = f.priceMin;
      if (f.priceMax !== PRICE_BOUNDS[1]) params.price_max = f.priceMax;
      params.sort = f.sort;

      const { data } = await api.get("/sessions", { params, signal: controller.signal });
      setResults(data.results);
      setResolvedLocation(data.resolved_location);
      setOrigin(data.origin);
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        toast.error("Couldn't load sessions right now — try again in a moment.");
      }
      return;
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  // Run search + sync URL whenever the applied filters change (debounced so slider drags / rapid clicks don't spam requests)
  useEffect(() => {
    const t = setTimeout(() => {
      runSearch(filters);
      if (skipNextSync.current) {
        skipNextSync.current = false;
      } else {
        setSearchParams(paramsFromFilters(filters), { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const submitLocation = (e) => {
    e?.preventDefault();
    setFilters((f) => ({ ...f, locationText: locationInput.trim(), lat: null, lng: null }));
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location detection.");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationInput("Current Location");
        setFilters((f) => ({ ...f, locationText: "", lat: pos.coords.latitude, lng: pos.coords.longitude }));
        setGeoLoading(false);
      },
      () => {
        toast.error("Couldn't get your location — check your browser permissions.");
        setGeoLoading(false);
      },
      { timeout: 10000 }
    );
  };

  const clearAll = () => {
    setLocationInput("");
    setFilters(DEFAULT_FILTERS);
  };

  const expandRadius = () => {
    const idx = RADIUS_OPTIONS.findIndex((r) => r.value === filters.radius);
    const next = RADIUS_OPTIONS[Math.min(idx + 1, RADIUS_OPTIONS.length - 1)];
    setFilters((f) => ({ ...f, radius: next.value }));
  };

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail.trim()) return;
    setNotifySubmitting(true);
    try {
      await api.post("/sessions/notify-me", {
        email: notifyEmail.trim(),
        location: filters.locationText || locationInput || "Anywhere",
        radius: filters.radius,
        session_type: filters.sessionType !== "all" ? filters.sessionType : null,
      });
      setNotifyDone(true);
    } catch {
      toast.error("Couldn't save that — try again in a moment.");
    } finally {
      setNotifySubmitting(false);
    }
  };

  const sessionTypeLabel = (key) => sessionTypes.find((t) => t.type_key === key)?.title || key;

  const hasLocation = !!(filters.locationText || filters.lat != null);
  const priceActive = filters.priceMin !== PRICE_BOUNDS[0] || filters.priceMax !== PRICE_BOUNDS[1];
  const dateLabel = filters.datePreset === "custom" ? (filters.customDate ? formatDate(filters.customDate) : "Choose a Date") : DATE_OPTIONS.find((d) => d.value === filters.datePreset)?.label;

  const chips = useMemo(() => {
    const c = [];
    if (hasLocation) c.push({ key: "location", label: resolvedLocation || (filters.lat != null ? "Current Location" : filters.locationText) });
    if (hasLocation && filters.radius !== "any") c.push({ key: "radius", label: `Within ${filters.radius} Miles` });
    if (filters.sessionType !== "all") c.push({ key: "sessionType", label: sessionTypeLabel(filters.sessionType) });
    if (filters.datePreset !== "any") c.push({ key: "date", label: dateLabel });
    if (priceActive) c.push({ key: "price", label: `$${filters.priceMin}–$${filters.priceMax}` });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, resolvedLocation, hasLocation, priceActive]);

  const removeChip = (key) => {
    if (key === "location") {
      setLocationInput("");
      setFilters((f) => ({ ...f, locationText: "", lat: null, lng: null }));
    } else if (key === "radius") setFilters((f) => ({ ...f, radius: "any" }));
    else if (key === "sessionType") setFilters((f) => ({ ...f, sessionType: "all" }));
    else if (key === "date") setFilters((f) => ({ ...f, datePreset: "any", customDate: "" }));
    else if (key === "price") setFilters((f) => ({ ...f, priceMin: PRICE_BOUNDS[0], priceMax: PRICE_BOUNDS[1] }));
  };

  const summaryText = () => {
    const count = results.length;
    const noun = `Stranger Session${count === 1 ? "" : "s"}`;
    if (hasLocation && filters.radius !== "any") return `${count} ${noun} within ${filters.radius} miles of ${resolvedLocation || "your location"}`;
    if (hasLocation) return `${count} ${noun} near ${resolvedLocation || "your location"}`;
    return `${count} ${noun} found`;
  };

  const secondaryFilters = (
    <div className="flex flex-col sm:flex-row sm:items-end gap-4">
      <div className="min-w-[180px]">
        <Label className="text-clean-white/70 text-xs font-bold uppercase tracking-wide mb-1.5 block">Date</Label>
        <Select value={filters.datePreset} onValueChange={(v) => setFilters((f) => ({ ...f, datePreset: v }))}>
          <SelectTrigger data-testid="filter-date" className="bg-white/10 text-clean-white border-white/20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATE_OPTIONS.map((d) => (
              <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filters.datePreset === "custom" && (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid="filter-custom-date-trigger" className="mt-2 text-xs font-bold text-stranger-orange hover:underline">
                {filters.customDate ? formatDate(filters.customDate) : "Pick a date →"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={filters.customDate ? new Date(`${filters.customDate}T00:00:00`) : undefined}
                onSelect={(d) => d && setFilters((f) => ({ ...f, customDate: d.toISOString().slice(0, 10) }))}
                disabled={(d) => d < new Date(new Date().toDateString())}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div className="min-w-[220px] flex-1">
        <Label className="text-clean-white/70 text-xs font-bold uppercase tracking-wide mb-1.5 block">
          Price {priceActive ? `($${filters.priceMin} – $${filters.priceMax})` : "(Any Price)"}
        </Label>
        <Slider
          data-testid="filter-price-slider"
          min={PRICE_BOUNDS[0]}
          max={PRICE_BOUNDS[1]}
          step={5}
          value={[filters.priceMin, filters.priceMax]}
          onValueChange={([lo, hi]) => setFilters((f) => ({ ...f, priceMin: lo, priceMax: hi }))}
          className="py-2.5"
        />
      </div>
      <div className="flex gap-3">
        <Button type="button" variant="ghost" data-testid="clear-filters-button" onClick={clearAll} className="text-clean-white/70 hover:text-clean-white font-bold text-xs uppercase tracking-wide">
          Clear Filters
        </Button>
      </div>
    </div>
  );

  return (
    <div data-testid="find-session-page">
      <section className="bg-midnight-black pt-16 pb-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-stranger-orange text-xs font-bold tracking-[0.25em] uppercase mb-3">For Strangers</p>
          <h1 className="text-clean-white font-extrabold uppercase tracking-tight text-3xl sm:text-4xl mb-8">Find a Stranger Session Near You</h1>

          <form onSubmit={submitLocation} className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="session-search-form">
            <div className="grid grid-cols-1 md:grid-cols-[2.5fr_1fr_1fr_auto] gap-3 items-end">
              <div>
                <Label htmlFor="location-input" className="text-clean-white/70 text-xs font-bold uppercase tracking-wide mb-1.5 block">Location</Label>
                <div className="relative">
                  <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-clean-white/40" />
                  <Input
                    id="location-input"
                    data-testid="filter-location"
                    placeholder="City, State or ZIP Code"
                    value={locationInput}
                    onChange={(e) => setLocationInput(e.target.value)}
                    className="bg-white/10 text-clean-white border-white/20 placeholder:text-white/40 pl-10"
                  />
                </div>
                <button type="button" data-testid="use-my-location-button" onClick={useMyLocation} disabled={geoLoading} className="mt-1.5 text-xs font-bold text-stranger-orange hover:underline flex items-center gap-1">
                  {geoLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />} Use My Location
                </button>
              </div>

              <div>
                <Label className="text-clean-white/70 text-xs font-bold uppercase tracking-wide mb-1.5 block">Distance</Label>
                <Select value={filters.radius} onValueChange={(v) => setFilters((f) => ({ ...f, radius: v }))}>
                  <SelectTrigger data-testid="filter-distance" className="bg-white/10 text-clean-white border-white/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RADIUS_OPTIONS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-clean-white/70 text-xs font-bold uppercase tracking-wide mb-1.5 block">Session Type</Label>
                <Select value={filters.sessionType} onValueChange={(v) => setFilters((f) => ({ ...f, sessionType: v }))}>
                  <SelectTrigger data-testid="filter-type" className="bg-white/10 text-clean-white border-white/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Session Types</SelectItem>
                    {sessionTypes.map((t) => (
                      <SelectItem key={t.type_key} value={t.type_key}>{t.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" data-testid="search-sessions-button" className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold tracking-wide rounded-full h-11 px-8 w-full md:w-auto">
                <Search className="w-4 h-4 mr-2" /> FIND SESSIONS
              </Button>
            </div>

            <div className="hidden sm:block mt-5 pt-5 border-t border-white/10">{secondaryFilters}</div>

            <div className="sm:hidden mt-4 flex items-center justify-between">
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button type="button" variant="outline" data-testid="mobile-filters-trigger" className="border-white/20 text-clean-white bg-white/10 hover:bg-white/20 font-bold text-xs uppercase tracking-wide rounded-full">
                    <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="bg-midnight-black border-white/10 max-h-[80vh] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle className="text-clean-white uppercase font-extrabold">Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">{secondaryFilters}</div>
                </SheetContent>
              </Sheet>
              <button type="button" data-testid="browse-all-link-mobile" onClick={clearAll} className="text-xs font-bold text-clean-white/70 hover:text-clean-white underline">
                Browse All Sessions
              </button>
            </div>
          </form>
          <button type="button" data-testid="browse-all-link" onClick={clearAll} className="hidden sm:inline-block mt-4 text-xs font-bold text-clean-white/60 hover:text-clean-white underline">
            Browse All Sessions
          </button>
        </div>
      </section>

      <section className="bg-clean-white py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <p className="font-extrabold uppercase text-sm tracking-wide" data-testid="results-summary">{loading ? "Searching..." : summaryText()}</p>
            <div className="flex items-center gap-2">
              <Label className="text-xs font-bold uppercase tracking-wide text-midnight-black/50 whitespace-nowrap">Sort By</Label>
              <Select value={filters.sort} onValueChange={(v) => setFilters((f) => ({ ...f, sort: v }))}>
                <SelectTrigger data-testid="filter-sort" className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.filter((s) => s.value !== "closest" || hasLocation).map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {chips.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="active-filter-chips">
              {chips.map((c) => (
                <button
                  key={c.key}
                  data-testid={`filter-chip-${c.key}`}
                  onClick={() => removeChip(c.key)}
                  className="flex items-center gap-1.5 bg-warm-white border border-soft-gray rounded-full pl-4 pr-2.5 py-1.5 text-xs font-bold text-midnight-black hover:border-stranger-orange transition-colors"
                >
                  {c.label} <X className="w-3.5 h-3.5" />
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <p className="text-center text-midnight-black/60 font-medium py-16" data-testid="sessions-loading">
              Loading sessions...
            </p>
          ) : results.length === 0 ? (
            <div className="text-center py-16 max-w-lg mx-auto" data-testid="no-sessions-found">
              <h3 className="font-extrabold uppercase text-xl mb-2">No Stranger Sessions near you yet.</h3>
              <p className="text-midnight-black/60 font-medium mb-8">
                We're growing. Want to know when a Stranger Session becomes available near you?
              </p>
              {!notifyDone ? (
                <form onSubmit={submitNotify} className="flex flex-col sm:flex-row gap-2 mb-6" data-testid="notify-me-form">
                  <Input
                    type="email"
                    required
                    placeholder="you@email.com"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    data-testid="notify-me-email-input"
                  />
                  <Button type="submit" disabled={notifySubmitting} data-testid="notify-me-submit" className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold tracking-widest text-xs rounded-full px-6 whitespace-nowrap">
                    {notifySubmitting ? "SAVING..." : "NOTIFY ME"}
                  </Button>
                </form>
              ) : (
                <p className="text-sm font-bold text-stranger-orange mb-6" data-testid="notify-me-success">You're on the list — we'll be in touch!</p>
              )}
              <div className="flex flex-wrap justify-center gap-3">
                {hasLocation && filters.radius !== "any" && (
                  <Button type="button" variant="outline" onClick={expandRadius} data-testid="expand-search-area-button" className="rounded-full font-bold text-xs">
                    EXPAND SEARCH AREA
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={clearAll} data-testid="browse-all-no-results" className="rounded-full font-bold text-xs">
                  BROWSE ALL SESSIONS
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="sessions-grid">
              {results.map((s) => (
                <SessionCard key={s.id} session={s} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
